import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  getSermons,
  createSermon,
  updateSermon,
  deleteSermon,
} from '../services/api';
import './AdminSermons.css';

const EMPTY_FORM = {
  title: '',
  speaker: '',
  date: '',
  series: '',
  scripture_reference: '',
  video_url: '',
  description: '',
};

/** Extract YouTube video ID from URL or bare ID */
function getYouTubeId(url) {
  if (!url) return null;
  if (/^[A-Za-z0-9_-]{11}$/.test(url.trim())) return url.trim();
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') return u.pathname.slice(1);
    if (u.searchParams.get('v')) return u.searchParams.get('v');
    const m = u.pathname.match(/\/embed\/([A-Za-z0-9_-]{11})/);
    if (m) return m[1];
  } catch (_) { /* not a valid URL */ }
  return null;
}

function getThumbnail(url) {
  const id = getYouTubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

function toInputDate(iso) {
  if (!iso) return '';
  return iso.slice(0, 10);
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

export default function AdminSermons() {
  const [sermons, setSermons] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const formRef = useRef(null);

  useEffect(() => { load(); }, []);

  function load() {
    getSermons().then(setSermons).catch(() => setError('Impossible de charger les sermons.'));
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  function startEdit(s) {
    setEditingId(s.id);
    setForm({
      title: s.title || '',
      speaker: s.speaker || '',
      date: toInputDate(s.date),
      series: s.series || '',
      scripture_reference: s.scripture_reference || '',
      video_url: s.video_url || '',
      description: s.description || '',
    });
    setError('');
    setSuccess('');
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) return setError('Le titre est requis.');
    if (!form.speaker.trim()) return setError('Le prédicateur est requis.');
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        date: form.date ? new Date(form.date).toISOString() : new Date().toISOString(),
      };
      if (editingId) {
        await updateSermon(editingId, payload);
        setSuccess('Sermon mis à jour.');
      } else {
        await createSermon(payload);
        setSuccess('Sermon ajouté.');
      }
      setEditingId(null);
      setForm(EMPTY_FORM);
      load();
    } catch {
      setError('Une erreur est survenue. Vérifiez les données.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(s) {
    if (!window.confirm(`Supprimer « ${s.title} » ?`)) return;
    try {
      await deleteSermon(s.id);
      setSuccess('Sermon supprimé.');
      load();
    } catch {
      setError('Erreur lors de la suppression.');
    }
  }

  const thumb = getThumbnail(form.video_url);

  return (
    <div className="as-page">
      {/* Header */}
      <div className="as-header">
        <div className="container">
          <div className="as-header__inner">
            <div>
              <Link to="/gestion-ecclesiale" className="as-breadcrumb">← Tableau de bord</Link>
              <h1 className="as-title">Gestion des Sermons</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="container as-body">

        {/* ── Form ── */}
        <section className="as-form-section" ref={formRef}>
          <h2 className="as-section-title">
            {editingId ? 'Modifier le sermon' : 'Ajouter un sermon'}
          </h2>

          {error && <div className="as-alert as-alert--error">{error}</div>}
          {success && <div className="as-alert as-alert--success">{success}</div>}

          <form className="as-form" onSubmit={handleSubmit} noValidate>
            <div className="as-row">
              <div className="as-field as-field--grow">
                <label>Titre *</label>
                <input name="title" value={form.title} onChange={handleChange} placeholder="Titre du sermon" />
              </div>
              <div className="as-field as-field--grow">
                <label>Prédicateur *</label>
                <input name="speaker" value={form.speaker} onChange={handleChange} placeholder="Nom du prédicateur" />
              </div>
            </div>

            <div className="as-row">
              <div className="as-field">
                <label>Date</label>
                <input type="date" name="date" value={form.date} onChange={handleChange} />
              </div>
              <div className="as-field as-field--grow">
                <label>Série</label>
                <input name="series" value={form.series} onChange={handleChange} placeholder="ex: Espérance Vivante" />
              </div>
              <div className="as-field as-field--grow">
                <label>Référence biblique</label>
                <input name="scripture_reference" value={form.scripture_reference} onChange={handleChange} placeholder="ex: Jean 3:16" />
              </div>
            </div>

            {/* YouTube URL + preview */}
            <div className="as-row as-row--align-start">
              <div className="as-field as-field--grow">
                <label>Lien YouTube</label>
                <input
                  name="video_url"
                  value={form.video_url}
                  onChange={handleChange}
                  placeholder="https://www.youtube.com/watch?v=... ou ID vidéo"
                />
                <span className="as-hint">URL complète ou ID YouTube (ex: dQw4w9WgXcQ)</span>
              </div>
              {thumb && (
                <div className="as-yt-preview">
                  <img src={thumb} alt="Aperçu YouTube" />
                  <span className="as-yt-badge">▶ YouTube</span>
                </div>
              )}
            </div>

            <div className="as-field">
              <label>Description</label>
              <textarea name="description" value={form.description} onChange={handleChange} rows={3} placeholder="Courte description du sermon..." />
            </div>

            <div className="as-form-actions">
              <button type="submit" className="as-btn as-btn--primary" disabled={saving}>
                {saving ? 'Enregistrement...' : editingId ? 'Mettre à jour' : 'Ajouter'}
              </button>
              {editingId && (
                <button type="button" className="as-btn as-btn--ghost" onClick={cancelEdit}>
                  Annuler
                </button>
              )}
            </div>
          </form>
        </section>

        {/* ── Table ── */}
        <section className="as-list-section">
          <h2 className="as-section-title">
            Sermons ({sermons.length})
          </h2>
          {sermons.length === 0 ? (
            <p className="as-empty">Aucun sermon enregistré.</p>
          ) : (
            <div className="as-table-wrap">
              <table className="as-table">
                <thead>
                  <tr>
                    <th>Aperçu</th>
                    <th>Titre</th>
                    <th>Prédicateur</th>
                    <th>Date</th>
                    <th>Série</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sermons.map((s) => {
                    const t = getThumbnail(s.video_url);
                    return (
                      <tr key={s.id} className={editingId === s.id ? 'as-row--editing' : ''}>
                        <td>
                          {t ? (
                            <img src={t} alt="" className="as-thumb" />
                          ) : (
                            <div className="as-thumb as-thumb--placeholder">🏛️</div>
                          )}
                        </td>
                        <td className="as-td-title">{s.title}</td>
                        <td>{s.speaker}</td>
                        <td className="as-td-date">{formatDate(s.date)}</td>
                        <td>{s.series || '—'}</td>
                        <td>
                          <button className="as-action-btn as-action-btn--edit" onClick={() => startEdit(s)}>Modifier</button>
                          <button className="as-action-btn as-action-btn--delete" onClick={() => handleDelete(s)}>Supprimer</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
